import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, ViewChild, Input, Output, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as i2 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as i1 from 'ngx-extended-pdf-viewer';
import { NgxExtendedPdfViewerComponent, NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { FsApiFile } from '@firestitch/api';
import { FsFile } from '@firestitch/file';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

class FsPdfViewerComponent {
    constructor(_ngxService, _cdRef) {
        this._ngxService = _ngxService;
        this._cdRef = _cdRef;
        this.backgroundColor = 'rgb(232, 232, 235)';
        this.pageViewMode = 'infinite-scroll';
        this.zoom = 'auto';
        this.init = new EventEmitter();
        this.pageRendered = new EventEmitter();
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        if (this.pageViewMode === 'infinite-scroll') {
            this.height = '100px';
        }
        this._initSrc();
    }
    pdfLoaded() {
        this.extendedPdfViewer.zoomToPageWidth = () => {
            return Promise.resolve();
        };
    }
    resize() {
        this._ngxService.recalculateSize();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _initSrc() {
        if (this.pdf instanceof FsApiFile) {
            this.pdf.blob
                .pipe(takeUntil(this._destroy$))
                .subscribe((blob) => {
                this.src = blob;
                this._cdRef.markForCheck();
            });
        }
        else if (this.pdf instanceof FsFile) {
            this.src = this.pdf.file;
        }
        else {
            this.src = this.pdf;
        }
    }
}
FsPdfViewerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPdfViewerComponent, deps: [{ token: i1.NgxExtendedPdfViewerService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsPdfViewerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsPdfViewerComponent, selector: "fs-pdf-viewer", inputs: { pdf: "pdf", height: "height", backgroundColor: "backgroundColor", pageViewMode: "pageViewMode", zoom: "zoom" }, outputs: { init: "init", pageRendered: "pageRendered" }, viewQueries: [{ propertyName: "extendedPdfViewer", first: true, predicate: NgxExtendedPdfViewerComponent, descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"src\">\n  <ngx-extended-pdf-viewer\n    [ngClass]=\" { 'zoom-page-width': zoom === 'page-width' }\"\n    class=\"mat-elevation-z2\"\n    [src]=\"src\"\n    [pageViewMode]=\"pageViewMode\"\n    [zoom]=\"zoom\"\n    [height]=\"height\"\n    [showToolbar]=\"false\"  \n    [backgroundColor]=\"backgroundColor\"\n    [sidebarVisible]=\"false\"\n    (pageRendered)=\"pageRendered.emit($event)\"\n    (pdfLoaded)=\"pdfLoaded()\">\n  </ngx-extended-pdf-viewer>\n</ng-container>\n<ng-container *ngIf=\"!src\">\n  <mat-progress-spinner\n    [mode]=\"'indeterminate'\">\n  </mat-progress-spinner>\n</ng-container>\n", styles: [":host ::ng-deep ngx-extended-pdf-viewer{--pdfViewer-padding-bottom: 0}:host ::ng-deep ngx-extended-pdf-viewer pdf-toolbar{display:none}:host ::ng-deep ngx-extended-pdf-viewer #viewerContainer{top:0px!important}:host ::ng-deep ngx-extended-pdf-viewer .pdfViewer{overflow:auto}:host ::ng-deep ngx-extended-pdf-viewer .pdfViewer:not(.removePageBorders) .page{margin-bottom:12px!important;border:none!important;box-shadow:0 3px 1px -2px #0003,0 2px 2px #00000024,0 1px 5px #0000001f}mat-progress-spinner{margin:100px auto 0}\n"], components: [{ type: i1.NgxExtendedPdfViewerComponent, selector: "ngx-extended-pdf-viewer", inputs: ["customFindbarInputArea", "customToolbar", "customFindbar", "customFindbarButtons", "customPdfViewer", "customSecondaryToolbar", "customSidebar", "customThumbnail", "customFreeFloatingBar", "showFreeFloatingBar", "enableDragAndDrop", "formData", "pageViewMode", "scrollMode", "authorization", "httpHeaders", "contextMenuAllowed", "enablePrint", "delayFirstView", "logLevel", "enablePinchOnMobile", "minifiedJSLibraries", "printResolution", "rotation", "src", "base64Src", "minHeight", "height", "useBrowserLocale", "forceUsingLegacyES5", "backgroundColor", "pdfBackground", "pdfBackgroundColorToReplace", "filenameForDownload", "ignoreKeyboard", "ignoreKeys", "acceptKeys", "imageResourcesPath", "localeFolderPath", "language", "listenToURL", "nameddest", "password", "showUnverifiedSignatures", "startTabindex", "showSidebarButton", "sidebarVisible", "showFindButton", "showFindHighlightAll", "showFindMatchCase", "showFindCurrentPageOnly", "showFindPageRange", "showFindEntireWord", "showFindEntirePhrase", "showFindIgnoreAccents", "showFindFuzzySearch", "showFindResultsCount", "showFindMessages", "showPagingButtons", "showZoomButtons", "showPresentationModeButton", "showOpenFileButton", "showPrintButton", "showDownloadButton", "showBookmarkButton", "theme", "formTheme", "showToolbar", "showSecondaryToolbarButton", "showRotateButton", "handTool", "showHandToolButton", "showScrollingButton", "showSpreadButton", "showPropertiesButton", "showBorders", "spread", "page", "pageLabel", "textLayer", "zoom", "zoomLevels", "maxZoom", "minZoom", "_mobileFriendlyZoom", "wheelAction", "mobileFriendlyZoom"], outputs: ["formDataChange", "progress", "srcChange", "scrollModeChange", "afterPrint", "beforePrint", "currentZoomFactor", "rotationChange", "sidebarVisibleChange", "handToolChange", "spreadChange", "thumbnailDrawn", "pageChange", "pageLabelChange", "pagesLoaded", "pageRender", "pageRendered", "pdfDownloaded", "pdfLoaded", "pdfLoadingStarts", "pdfLoadingFailed", "textLayerRendered", "updateFindMatchesCount", "updateFindState", "zoomChange"] }, { type: i2.MatProgressSpinner, selector: "mat-progress-spinner", inputs: ["color", "mode", "diameter", "strokeWidth", "value"], exportAs: ["matProgressSpinner"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPdfViewerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-pdf-viewer',
                    templateUrl: './pdf-viewer.component.html',
                    styleUrls: ['./pdf-viewer.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i1.NgxExtendedPdfViewerService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { extendedPdfViewer: [{
                type: ViewChild,
                args: [NgxExtendedPdfViewerComponent]
            }], pdf: [{
                type: Input
            }], height: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], pageViewMode: [{
                type: Input
            }], zoom: [{
                type: Input
            }], init: [{
                type: Output
            }], pageRendered: [{
                type: Output
            }] } });

class FsPdfViewerModule {
}
FsPdfViewerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPdfViewerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsPdfViewerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPdfViewerModule, declarations: [FsPdfViewerComponent], imports: [CommonModule,
        FormsModule,
        MatProgressSpinnerModule,
        NgxExtendedPdfViewerModule], exports: [FsPdfViewerComponent] });
FsPdfViewerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPdfViewerModule, imports: [[
            CommonModule,
            FormsModule,
            MatProgressSpinnerModule,
            NgxExtendedPdfViewerModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPdfViewerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        MatProgressSpinnerModule,
                        NgxExtendedPdfViewerModule,
                    ],
                    exports: [
                        FsPdfViewerComponent,
                    ],
                    declarations: [
                        FsPdfViewerComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FsPdfViewerComponent, FsPdfViewerModule };
